import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ProjectForm } from '../components/ProjectForm';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  ArrowLeft,
  Edit,
  Target,
  Calendar,
  DollarSign,
  Users,
  MessageSquare,
  CheckCircle2,
  Clock,
  TrendingUp,
  MapPin,
  Building2,
  Megaphone,
  FileText,
  ListTodo,
  CalendarDays,
  Loader2,
  AlertCircle,
  Inbox
} from 'lucide-react';
import { format, parseISO, differenceInDays, isPast } from 'date-fns';

const STATUS_CONFIG = {
  planning: { label: 'Planning', color: 'bg-slate-100 text-slate-700' },
  active: { label: 'Active', color: 'bg-blue-50 text-blue-700' },
  on_hold: { label: 'On Hold', color: 'bg-amber-50 text-amber-700' },
  completed: { label: 'Completed', color: 'bg-emerald-50 text-emerald-700' }
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-slate-100 text-slate-600', dotColor: 'bg-slate-400' },
  medium: { label: 'Medium', color: 'bg-blue-50 text-blue-600', dotColor: 'bg-blue-400' },
  high: { label: 'High', color: 'bg-amber-50 text-amber-600', dotColor: 'bg-amber-400' },
  critical: { label: 'Critical', color: 'bg-red-50 text-red-600', dotColor: 'bg-red-400' }
};

const InfoRow = ({ icon: Icon, label, value, className = '' }) => {
  if (!value) return null;
  return (
    <div className={`flex items-start gap-3 ${className}`}>
      <Icon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
      <div>
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-sm text-slate-900 font-medium">{value}</p>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, subValue, className = '', iconColor = 'text-slate-500' }) => (
  <div className={`p-4 rounded-lg bg-slate-50 ${className}`}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className={`w-4 h-4 ${iconColor}`} />
      <span className="text-xs text-slate-500">{label}</span>
    </div>
    <p className="text-xl font-semibold text-slate-900">{value}</p>
    {subValue && <p className="text-xs text-slate-500 mt-1">{subValue}</p>}
  </div>
);

export const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const { subscribe } = useWebSocket();
  const { can } = usePermissions();
  
  const [project, setProject] = useState(null);
  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [content, setContent] = useState([]);
  const [events, setEvents] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = useCallback(async () => {
    try {
      const [projectRes, usersRes, tasksRes, contentRes, eventsRes, requestsRes] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get('/users'),
        api.get(`/tasks?project_id=${projectId}`),
        api.get(`/content?project_id=${projectId}`),
        api.get(`/events?project_id=${projectId}`),
        api.get(`/requests?project_id=${projectId}`)
      ]);
      setProject(projectRes.data);
      setUsers(usersRes.data);
      setTasks(tasksRes.data);
      setContent(contentRes.data);
      setEvents(eventsRes.data);
      setRequests(requestsRes.data);
    } catch (error) {
      console.error('Failed to fetch project:', error);
    } finally {
      setLoading(false);
    }
  }, [api, projectId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Real-time updates
  useEffect(() => {
    const unsubscribes = [];
    
    unsubscribes.push(subscribe('project_updated', (message) => {
      if (message.data?.id === projectId) {
        setProject(prev => ({ ...prev, ...message.data }));
      }
    }));
    
    unsubscribes.push(subscribe('task_created', (message) => {
      if (message.data?.project_id === projectId) {
        setTasks(prev => [...prev, message.data]);
      }
    }));
    
    unsubscribes.push(subscribe('task_updated', (message) => {
      if (message.data?.project_id === projectId) {
        setTasks(prev => prev.map(t => t.id === message.data.id ? message.data : t));
      }
    }));

    return () => unsubscribes.forEach(unsub => unsub());
  }, [subscribe, projectId]);

  const handleUpdate = async (data) => {
    await api.put(`/projects/${projectId}`, data);
    setEditDialogOpen(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertCircle className="w-12 h-12 text-slate-300 mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Project not found</h3>
        <Button variant="outline" onClick={() => navigate('/projects')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Projects
        </Button>
      </div>
    );
  }

  const owner = users.find(u => u.id === project.owner_id);
  const completedTasks = tasks.filter(t => t.status === 'completed').length;
  const totalTasks = tasks.length;
  const taskProgress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  
  const completedMilestones = (project.milestones || []).filter(m => m.completed).length;
  const totalMilestones = (project.milestones || []).length;
  const milestoneProgress = totalMilestones > 0 ? Math.round((completedMilestones / totalMilestones) * 100) : 0;
  
  const totalAllocated = (project.allocated_spend || []).reduce((sum, item) => sum + (item.amount || 0), 0);
  const budgetUtilization = project.total_budget ? Math.round((totalAllocated / project.total_budget) * 100) : 0;

  const daysUntilLaunch = project.launch_date ? differenceInDays(parseISO(project.launch_date), new Date()) : null;
  const daysUntilEnd = project.end_date ? differenceInDays(parseISO(project.end_date), new Date()) : null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="project-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/projects')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">{project.name}</h1>
              <Badge className={STATUS_CONFIG[project.status]?.color}>
                {STATUS_CONFIG[project.status]?.label}
              </Badge>
              <Badge variant="outline" className={PRIORITY_CONFIG[project.priority]?.color}>
                {PRIORITY_CONFIG[project.priority]?.label}
              </Badge>
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-500">
              {project.campaign_type && (
                <span className="flex items-center gap-1">
                  <Megaphone className="w-3.5 h-3.5" />
                  {project.campaign_type}
                </span>
              )}
              {project.department && (
                <span className="flex items-center gap-1">
                  <Building2 className="w-3.5 h-3.5" />
                  {project.department}
                </span>
              )}
              {owner && (
                <span className="flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" />
                  {owner.name}
                </span>
              )}
            </div>
          </div>
        </div>
        
        {can('project:update') && (
          <Button onClick={() => setEditDialogOpen(true)} data-testid="edit-project-btn">
            <Edit className="w-4 h-4 mr-2" />
            Edit Project
          </Button>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={ListTodo}
          label="Tasks Progress"
          value={`${completedTasks}/${totalTasks}`}
          subValue={`${taskProgress}% complete`}
          iconColor="text-blue-500"
        />
        <StatCard
          icon={CheckCircle2}
          label="Milestones"
          value={`${completedMilestones}/${totalMilestones}`}
          subValue={`${milestoneProgress}% complete`}
          iconColor="text-emerald-500"
        />
        <StatCard
          icon={DollarSign}
          label="Budget Used"
          value={`$${totalAllocated.toLocaleString()}`}
          subValue={project.total_budget ? `${budgetUtilization}% of $${project.total_budget.toLocaleString()}` : 'No budget set'}
          iconColor="text-amber-500"
        />
        <StatCard
          icon={Clock}
          label={daysUntilLaunch !== null ? (daysUntilLaunch >= 0 ? 'Days to Launch' : 'Days Since Launch') : 'Days Remaining'}
          value={daysUntilLaunch !== null ? Math.abs(daysUntilLaunch) : (daysUntilEnd !== null ? Math.abs(daysUntilEnd) : '-')}
          subValue={project.launch_date ? format(parseISO(project.launch_date), 'MMM d, yyyy') : (project.end_date ? format(parseISO(project.end_date), 'MMM d, yyyy') : 'No date set')}
          iconColor="text-purple-500"
        />
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="overview" data-testid="detail-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="detail-tab-timeline">Timeline & Milestones</TabsTrigger>
          <TabsTrigger value="budget" data-testid="detail-tab-budget">Budget & ROI</TabsTrigger>
          <TabsTrigger value="audience" data-testid="detail-tab-audience">Target Audience</TabsTrigger>
          <TabsTrigger value="messaging" data-testid="detail-tab-messaging">Messaging</TabsTrigger>
          <TabsTrigger value="assets" data-testid="detail-tab-assets">Tasks & Content</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Target className="w-4 h-4" />
                  Project Overview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {project.description && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Description</h4>
                    <p className="text-sm text-slate-600">{project.description}</p>
                  </div>
                )}
                
                {project.business_objective && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Business Objective</h4>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700">
                      {project.business_objective}
                    </Badge>
                  </div>
                )}
                
                {project.strategic_alignment && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Strategic Alignment</h4>
                    <p className="text-sm text-slate-600">{project.strategic_alignment}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <InfoRow icon={Calendar} label="Start Date" value={project.start_date ? format(parseISO(project.start_date), 'MMM d, yyyy') : null} />
                  <InfoRow icon={Calendar} label="Launch Date" value={project.launch_date ? format(parseISO(project.launch_date), 'MMM d, yyyy') : null} />
                  <InfoRow icon={Calendar} label="End Date" value={project.end_date ? format(parseISO(project.end_date), 'MMM d, yyyy') : null} />
                  <InfoRow icon={Building2} label="Department" value={project.department} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <TrendingUp className="w-4 h-4" />
                  Progress
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-600">Tasks</span>
                    <span className="font-medium">{taskProgress}%</span>
                  </div>
                  <Progress value={taskProgress} className="h-2" />
                  <p className="text-xs text-slate-500 mt-1">{completedTasks} of {totalTasks} tasks completed</p>
                </div>

                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-600">Milestones</span>
                    <span className="font-medium">{milestoneProgress}%</span>
                  </div>
                  <Progress value={milestoneProgress} className="h-2" />
                  <p className="text-xs text-slate-500 mt-1">{completedMilestones} of {totalMilestones} milestones completed</p>
                </div>

                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-600">Budget</span>
                    <span className={`font-medium ${budgetUtilization > 100 ? 'text-red-600' : ''}`}>{budgetUtilization}%</span>
                  </div>
                  <Progress value={Math.min(budgetUtilization, 100)} className={`h-2 ${budgetUtilization > 100 ? '[&>div]:bg-red-500' : ''}`} />
                  <p className="text-xs text-slate-500 mt-1">
                    ${totalAllocated.toLocaleString()} of ${(project.total_budget || 0).toLocaleString()} allocated
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Calendar className="w-4 h-4" />
                  Project Timeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-3 h-3 rounded-full bg-blue-500" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-700">Start Date</p>
                      <p className="text-sm text-slate-500">
                        {project.start_date ? format(parseISO(project.start_date), 'MMMM d, yyyy') : 'Not set'}
                      </p>
                    </div>
                  </div>
                  
                  {project.launch_date && (
                    <div className="flex items-center gap-4">
                      <div className="w-3 h-3 rounded-full bg-amber-500" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-700">Launch Date</p>
                        <p className="text-sm text-slate-500">
                          {format(parseISO(project.launch_date), 'MMMM d, yyyy')}
                          {daysUntilLaunch !== null && (
                            <span className={`ml-2 ${daysUntilLaunch < 0 ? 'text-emerald-600' : daysUntilLaunch <= 7 ? 'text-amber-600' : ''}`}>
                              ({daysUntilLaunch < 0 ? 'Launched' : `${daysUntilLaunch} days away`})
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-4">
                    <div className="w-3 h-3 rounded-full bg-emerald-500" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-700">End Date</p>
                      <p className="text-sm text-slate-500">
                        {project.end_date ? format(parseISO(project.end_date), 'MMMM d, yyyy') : 'Not set'}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <CheckCircle2 className="w-4 h-4" />
                  Key Milestones ({completedMilestones}/{totalMilestones})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(project.milestones || []).length > 0 ? (
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-3">
                      {project.milestones.map((milestone) => (
                        <div 
                          key={milestone.id} 
                          className={`flex items-start gap-3 p-3 rounded-lg ${milestone.completed ? 'bg-emerald-50' : 'bg-slate-50'}`}
                          data-testid={`detail-milestone-${milestone.id}`}
                        >
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
                            milestone.completed ? 'bg-emerald-500 text-white' : 'border-2 border-slate-300'
                          }`}>
                            {milestone.completed && <CheckCircle2 className="w-3 h-3" />}
                          </div>
                          <div className="flex-1">
                            <p className={`font-medium ${milestone.completed ? 'text-emerald-700' : 'text-slate-700'}`}>
                              {milestone.name}
                            </p>
                            {milestone.target_date && (
                              <p className={`text-xs ${
                                milestone.completed 
                                  ? 'text-emerald-600' 
                                  : isPast(parseISO(milestone.target_date)) 
                                    ? 'text-red-600' 
                                    : 'text-slate-500'
                              }`}>
                                {format(parseISO(milestone.target_date), 'MMM d, yyyy')}
                                {!milestone.completed && isPast(parseISO(milestone.target_date)) && ' (Overdue)'}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <CheckCircle2 className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p>No milestones defined</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Budget Tab */}
        <TabsContent value="budget">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <DollarSign className="w-4 h-4" />
                  Budget Overview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">Total Budget</p>
                    <p className="text-2xl font-bold text-slate-900">
                      ${(project.total_budget || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">Allocated</p>
                    <p className={`text-2xl font-bold ${budgetUtilization > 100 ? 'text-red-600' : 'text-slate-900'}`}>
                      ${totalAllocated.toLocaleString()}
                    </p>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span>Budget Utilization</span>
                    <span className={budgetUtilization > 100 ? 'text-red-600 font-medium' : ''}>{budgetUtilization}%</span>
                  </div>
                  <Progress value={Math.min(budgetUtilization, 100)} className={`h-3 ${budgetUtilization > 100 ? '[&>div]:bg-red-500' : ''}`} />
                  {project.total_budget && (
                    <p className="text-xs text-slate-500 mt-2">
                      ${(project.total_budget - totalAllocated).toLocaleString()} remaining
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-600 mb-1">Projected ROI</p>
                    <p className="text-xl font-bold text-blue-700">
                      {project.projected_roi ? `${project.projected_roi}%` : '-'}
                    </p>
                  </div>
                  <div className="p-4 bg-emerald-50 rounded-lg">
                    <p className="text-xs text-emerald-600 mb-1">Actual ROI</p>
                    <p className="text-xl font-bold text-emerald-700">
                      {project.actual_roi ? `${project.actual_roi}%` : '-'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  Budget Allocation
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(project.allocated_spend || []).length > 0 ? (
                  <div className="space-y-3">
                    {project.allocated_spend.map((item, index) => {
                      const percentage = project.total_budget 
                        ? Math.round((item.amount / project.total_budget) * 100) 
                        : 0;
                      return (
                        <div key={index} className="space-y-1.5">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-700">{item.category}</span>
                            <span className="text-slate-600">${item.amount.toLocaleString()} ({percentage}%)</span>
                          </div>
                          <Progress value={percentage} className="h-2" />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <DollarSign className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p>No budget allocation defined</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Audience Tab */}
        <TabsContent value="audience">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="w-4 h-4" />
                Target Audience
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <InfoRow icon={Users} label="Primary Audience" value={project.primary_audience} />
                  <InfoRow icon={Users} label="Secondary Audience" value={project.secondary_audience} />
                  <InfoRow icon={MapPin} label="Geography" value={project.geography} />
                  <InfoRow icon={Building2} label="Industry Segment" value={project.industry_segment} />
                </div>
                
                {project.buyer_persona && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4 text-slate-400" />
                      Buyer Persona
                    </h4>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-600 whitespace-pre-line">{project.buyer_persona}</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Messaging Tab */}
        <TabsContent value="messaging">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <MessageSquare className="w-4 h-4" />
                  Messaging Strategy
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {project.value_proposition && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Core Value Proposition</h4>
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-800">{project.value_proposition}</p>
                    </div>
                  </div>
                )}

                {(project.key_messages || []).length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Key Message Pillars</h4>
                    <div className="flex flex-wrap gap-2">
                      {project.key_messages.map((msg, index) => (
                        <Badge key={index} variant="secondary" className="py-1.5 px-3">
                          {msg}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {project.call_to_action && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Primary Call-to-Action</h4>
                    <Badge className="bg-emerald-100 text-emerald-800 py-1.5 px-3 text-sm">
                      {project.call_to_action}
                    </Badge>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Megaphone className="w-4 h-4" />
                  Creative Direction
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {project.tone_of_voice && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Tone of Voice</h4>
                    <Badge variant="outline" className="py-1.5 px-3">
                      {project.tone_of_voice}
                    </Badge>
                  </div>
                )}

                {project.brand_guidelines && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Brand Guidelines Notes</h4>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-600 whitespace-pre-line">{project.brand_guidelines}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tasks & Content Tab */}
        <TabsContent value="assets">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base">
                  <ListTodo className="w-4 h-4" />
                  Tasks ({tasks.length})
                </CardTitle>
                <Link to="/tasks">
                  <Button variant="outline" size="sm">View All</Button>
                </Link>
              </CardHeader>
              <CardContent>
                {tasks.length > 0 ? (
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-2">
                      {tasks.map(task => (
                        <div key={task.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                          <div className={`w-2 h-2 rounded-full ${PRIORITY_CONFIG[task.priority]?.dotColor || 'bg-slate-400'}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate">{task.name}</p>
                            <p className="text-xs text-slate-500">{task.status}</p>
                          </div>
                          {task.due_date && (
                            <span className={`text-xs ${isPast(parseISO(task.due_date)) && task.status !== 'completed' ? 'text-red-600' : 'text-slate-500'}`}>
                              {format(parseISO(task.due_date), 'MMM d')}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <ListTodo className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p>No tasks for this project</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base">
                  <FileText className="w-4 h-4" />
                  Content ({content.length})
                </CardTitle>
                <Link to="/calendar">
                  <Button variant="outline" size="sm">View Calendar</Button>
                </Link>
              </CardHeader>
              <CardContent>
                {content.length > 0 ? (
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-2">
                      {content.map(item => (
                        <div key={item.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate">{item.title}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">{item.content_type}</Badge>
                              <span className="text-xs text-slate-500">{item.status}</span>
                            </div>
                          </div>
                          {item.publish_date && (
                            <span className="text-xs text-slate-500">
                              {format(parseISO(item.publish_date), 'MMM d')}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p>No content for this project</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Linked Requests */}
            <Card className="lg:col-span-2" data-testid="project-requests-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Inbox className="w-4 h-4" />
                  Requests ({requests.length})
                </CardTitle>
                <Link to="/requests">
                  <Button variant="outline" size="sm">View All</Button>
                </Link>
              </CardHeader>
              <CardContent>
                {requests.length > 0 ? (
                  <div className="space-y-2">
                    {requests.map(req => (
                      <Link 
                        key={req.id} 
                        to={`/requests/${req.id}`}
                        className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                        data-testid={`project-request-${req.id}`}
                      >
                        <div className={`w-2 h-2 rounded-full ${
                          req.status === 'completed' ? 'bg-emerald-400' : 
                          req.is_sla_breached ? 'bg-red-400' : 'bg-blue-400'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-700 truncate">{req.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">{req.request_type}</Badge>
                            <span className="text-xs text-slate-500">{req.status?.replace(/_/g, ' ')}</span>
                          </div>
                        </div>
                        <span className="text-xs text-slate-500">
                          {req.primary_assignee_name || req.assigned_to_name || 'Unassigned'}
                        </span>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Inbox className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p>No requests linked to this project</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
          </DialogHeader>
          <ProjectForm
            initialData={project}
            users={users}
            onSubmit={handleUpdate}
            onCancel={() => setEditDialogOpen(false)}
            isEditing={true}
            currentUserId={user?.id}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};
